import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { createApi } from "../api/client";

type Job = Awaited<ReturnType<ReturnType<typeof createApi>["getJob"]>>;

const PROGRESS: Record<Job["status"], { label: string; pct: number }> = {
  pending:    { label: "Waiting for payment…",              pct: 10 },
  paid:       { label: "Payment received. Starting…",       pct: 20 },
  processing: { label: "Bringing your photo to life…",      pct: 65 },
  ready:      { label: "Your story is ready.",              pct: 100 },
  failed:     { label: "Something went wrong.",             pct: 0 },
};

/** Rotating "we're working on it" lines — keeps the wait from feeling boring
 * on a 60–90s render. Tone: cinematic, warm, slightly poetic — not jokey. */
const PROCESSING_MESSAGES: string[] = [
  "Bringing your photo to life…",
  "Restoring the colours time had faded…",
  "Listening for the moment in the photograph…",
  "Catching the light just so…",
  "Coaxing the past into motion…",
  "Setting the camera. Framing the shot…",
  "Finding the warmth in the frame…",
  "Letting the afternoon breathe…",
  "Composing the scene…",
  "Polishing every pixel of the original…",
  "Reading the photograph back to itself…",
  "Almost there. The last few frames…",
];

/** Shuffle once per page load so each user sees a different order. */
function shuffleOnce<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ROTATE_MS = 5500;     // total time per message before swapping
const TYPE_MS_PER_CHAR = 38; // typing speed — ~38ms feels like a thoughtful typist

export function Status() {
  const { id } = useParams<{ id: string }>();
  const { getIdToken } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creatingVariation, setCreatingVariation] = useState(false);
  const [buyingExtras, setBuyingExtras] = useState(false);

  const api = createApi(getIdToken);
  const justBoughtExtras = search.get("extras") === "ok";

  // Cycle the "processing" copy so the wait doesn't feel like a single static
  // line. Shuffled per session so each user gets fresh ordering.
  const messages = useMemo(() => shuffleOnce(PROCESSING_MESSAGES), []);
  const [messageIdx, setMessageIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [typingDone, setTypingDone] = useState(false);

  const fetchOnce = useCallback(async () => {
    if (!id) return;
    try {
      const j = await api.getJob(id);
      setJob(j);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load job");
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchOnce(); }, [fetchOnce]);

  useEffect(() => {
    if (!job || job.status === "ready" || job.status === "failed") return;
    const t = setInterval(fetchOnce, 4000);
    return () => clearInterval(t);
  }, [job, fetchOnce]);

  // Rotate the playful processing line every ROTATE_MS while the job is mid-flight.
  useEffect(() => {
    if (!job || (job.status !== "processing" && job.status !== "paid")) return;
    const t = setInterval(() => setMessageIdx(i => i + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typewriter effect for the current rotating message.
  useEffect(() => {
    if (!job || (job.status !== "processing" && job.status !== "paid")) return;
    const full = messages[messageIdx % messages.length];
    setTyped("");
    setTypingDone(false);
    let i = 0;
    const t = setInterval(() => {
      i++;
      setTyped(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(t);
        setTypingDone(true);
      }
    }, TYPE_MS_PER_CHAR);
    return () => clearInterval(t);
  }, [messageIdx, job?.status, messages]);

  const onMakeVariation = async () => {
    if (!id) return;
    setCreatingVariation(true); setError(null);
    try {
      const r = await api.createVariation(id);
      nav(`/jobs/${r.job_id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not start variation");
      setCreatingVariation(false);
    }
  };

  const onBuyExtras = async () => {
    if (!id) return;
    setBuyingExtras(true); setError(null);
    try {
      const r = await api.purchaseExtraVariations(id);
      window.location.href = r.checkout_url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not start checkout");
      setBuyingExtras(false);
    }
  };

  if (error) return <Centered text={error} tone="error" />;
  if (!job) return <Centered text="Loading…" />;

  const step = PROGRESS[job.status];
  const remaining = job.variations_remaining ?? 0;
  const isVariation = !!job.parent_job_id;
  const isWaiting = job.status === "processing" || job.status === "paid";

  return (
    <section className="mx-auto max-w-2xl px-6 py-12">
      {isVariation && (
        <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">
          Free variation {job.variation_index ?? ""}
        </p>
      )}
      {isWaiting ? (
        <h1 className="font-serif text-4xl tracking-tight min-h-[1.2em]">
          {typed}
          <span
            aria-hidden
            className={`inline-block w-[3px] h-[0.9em] align-[-0.05em] ml-1 bg-current ${
              typingDone ? "animate-[b2l-blink_900ms_steps(1)_infinite]" : ""
            }`}
          />
        </h1>
      ) : (
        <h1
          key={job.status}
          className="font-serif text-4xl tracking-tight animate-[b2l-fade_500ms_ease-out]"
        >
          {step.label}
        </h1>
      )}
      {job.status !== "ready" && job.status !== "failed" && (
        <div className="mt-6 h-2 rounded-full bg-neutral-200 overflow-hidden">
          <div
            className="h-full bg-black transition-all duration-500"
            style={{ width: `${step.pct}%` }}
          />
        </div>
      )}

      {justBoughtExtras && (
        <p className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          More variations unlocked. You can run another take below.
        </p>
      )}

      {job.status === "ready" && job.video_url && (
        <div className="mt-8">
          <video src={job.video_url} controls className="w-full rounded-xl shadow-sm" autoPlay />
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={job.download_url || job.video_url}
              className="inline-flex rounded-full bg-black text-white px-6 py-2.5 text-sm font-medium hover:bg-neutral-800 transition"
            >
              Download MP4
            </a>
            {remaining > 0 && (
              <button
                onClick={onMakeVariation}
                disabled={creatingVariation}
                className="inline-flex rounded-full border border-neutral-300 bg-white text-neutral-900 px-6 py-2.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 transition"
              >
                {creatingVariation
                  ? "Starting variation…"
                  : `Create free variation (${remaining} left)`}
              </button>
            )}
          </div>
          {remaining > 0 && (
            <p className="mt-3 text-xs text-neutral-500">
              A variation is a different cinematic take on the same photo — different
              camera work, a different moment on the face. Free, no extra payment.
            </p>
          )}

          {/* Post-checkout upsell: buy more variations once free ones are used */}
          {remaining === 0 && (
            <div className="mt-8 rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
              <p className="font-serif text-lg text-neutral-900">
                Want a few more takes?
              </p>
              <p className="mt-1 text-sm text-neutral-600">
                Add 3 more variations of this scene — different camera, different
                moment, same photo. Picked the best one yet? Save it before you go.
              </p>
              <button
                onClick={onBuyExtras}
                disabled={buyingExtras}
                className="mt-4 inline-flex rounded-full bg-black text-white px-6 py-2.5 text-sm font-medium hover:bg-neutral-800 disabled:opacity-50 transition"
              >
                {buyingExtras ? "Starting checkout…" : "Add 3 more variations"}
              </button>
            </div>
          )}
        </div>
      )}
      {job.status === "failed" && job.error && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {job.error}
        </p>
      )}
    </section>
  );
}

function Centered({ text, tone }: { text: string; tone?: "error" }) {
  return (
    <div className={`p-12 text-center ${tone === "error" ? "text-red-600" : "text-neutral-500"}`}>
      {text}
    </div>
  );
}
